import { useEffect, useState } from 'react';

/**
 * Shared responsive breakpoints.
 *
 * These mirror the Tailwind screens the portals already use so a component can
 * branch in JavaScript on exactly the same widths its classes branch on. Keep
 * them in sync with `tailwind-preset.ts` if the screens ever change.
 */
export const BREAKPOINTS = {
  /** Phones. Below Tailwind `md`. */
  mobile: '(max-width: 767px)',
  /** Phones and portrait tablets. Below Tailwind `lg`. */
  compact: '(max-width: 1023px)',
  /** Below Tailwind `xl` — where the two-column record workspaces collapse. */
  narrowWorkspace: '(max-width: 1279px)',
  /** Touch-first devices, regardless of width. */
  touch: '(pointer: coarse)',
} as const;

function matches(query: string): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
}

/**
 * Subscribe to a media query.
 *
 * Server/test environments without `matchMedia` resolve to `false`, which keeps
 * the desktop layout as the non-JS default and avoids a mobile flash.
 */
export function useMediaQuery(query: string): boolean {
  const [active, setActive] = useState(() => matches(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const update = (event: MediaQueryList | MediaQueryListEvent) => setActive(event.matches);
    update(list);
    // Safari < 14 only supports the deprecated listener API.
    if (typeof list.addEventListener === 'function') {
      list.addEventListener('change', update);
      return () => list.removeEventListener('change', update);
    }
    list.addListener(update);
    return () => list.removeListener(update);
  }, [query]);

  return active;
}

/** True on phone widths (below Tailwind `md`). */
export function useIsMobile(): boolean {
  return useMediaQuery(BREAKPOINTS.mobile);
}

/** True on phones and portrait tablets (below Tailwind `lg`). */
export function useIsCompact(): boolean {
  return useMediaQuery(BREAKPOINTS.compact);
}

/** True on touch-first devices. Use for hit-target and hover-affordance decisions. */
export function useIsTouch(): boolean {
  return useMediaQuery(BREAKPOINTS.touch);
}
